import React, { useState } from 'react';
import { Send, FileText, Paperclip, X, Square, Library } from 'lucide-react';
import { SUPPORTED_ACCEPT_STRING, type AttachedFile } from '../../hooks/useFileAttachment';
import DocumentPickerModal from './DocumentPickerModal';

interface ChatInputAreaProps {
  input: string;
  onInputChange: (value: string) => void;
  onSend: () => void;
  /** Abort the in-flight stream. Hook owns the AbortController; this is the
   * caller-side bind. Ignored when `isStreaming` is false. */
  onAbort: () => void;
  isStreaming: boolean;
  isPendingResponse: boolean;
  /** Block sends even when not streaming — set by GENERATION_IN_PROGRESS /
   * QUOTA_EXCEEDED error dispatchers. Cleared manually or after Retry-After. */
  blockSend?: boolean;
  attachedFiles: AttachedFile[];
  onFileAttach: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onRemoveFile: (index: number) => void;
  fileInputRef: React.RefObject<HTMLInputElement | null>;
  promptLabel: string;
  promptLabels: string[];
  onPromptLabelChange: (label: string) => void;
  /** Current user's ID — passed to DocumentPickerModal to fetch their docs.
   * When undefined, the [문서 참조] button is hidden (no valid user selected). */
  selectedUserId?: string | null;
  /** Called once per selected document from the picker modal. Wire to
   * `useFileAttachment.attachDocumentId`. */
  onAttachDocumentId?: (params: { documentId: string; name: string; media_type: string }) => void;
}

const MAX_ATTACHMENTS = 5;

const ChatInputArea: React.FC<ChatInputAreaProps> = React.memo(({
  input,
  onInputChange,
  onSend,
  onAbort,
  isStreaming,
  isPendingResponse,
  blockSend = false,
  attachedFiles,
  onFileAttach,
  onRemoveFile,
  fileInputRef,
  promptLabel,
  promptLabels,
  onPromptLabelChange,
  selectedUserId,
  onAttachDocumentId,
}) => {
  const [docPickerOpen, setDocPickerOpen] = useState(false);
  const canAttachMore = attachedFiles.length < MAX_ATTACHMENTS;
  const docRefEnabled = Boolean(selectedUserId && onAttachDocumentId);

  return (
  <div className="border-t border-slate-200 p-4 bg-white">
    {/* Attached files preview — distinct styling for base64 vs document_id modes
        so users can tell at a glance whether the bytes are being uploaded inline
        or referenced. */}
    {attachedFiles.length > 0 && (
      <div className="flex flex-wrap gap-2 mb-3 p-2 bg-slate-50 rounded-lg">
        {attachedFiles.map((f, idx) => (
          <div
            key={idx}
            className="relative group"
            data-testid={f.preview ? 'attachment-uploaded' : 'attachment-document_id'}
          >
            {f.preview ? (
              // 파일 선택 직후 업로드된 image — 로컬 preview URL 표시.
              <img
                src={f.preview}
                alt={f.name}
                className="w-16 h-16 object-cover rounded-lg border border-slate-200"
              />
            ) : f.media_type.startsWith('image/') ? (
              // 문서 참조로 추가한 image (preview 없음) — REF 아이콘.
              <div className="w-16 h-16 rounded-lg border-2 border-indigo-300 bg-indigo-50 flex flex-col items-center justify-center gap-0.5">
                <Library size={20} className="text-indigo-600" />
                <span className="text-[9px] text-indigo-700 font-semibold">REF</span>
              </div>
            ) : (
              <div className="w-16 h-16 rounded-lg border border-slate-200 bg-white flex flex-col items-center justify-center gap-1">
                <FileText size={20} className="text-red-500" />
                <span className="text-[9px] text-slate-500 max-w-[56px] truncate">PDF</span>
              </div>
            )}
            <button
              onClick={() => onRemoveFile(idx)}
              className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
            >
              <X size={12} />
            </button>
            <div className="text-[9px] text-slate-500 mt-0.5 max-w-[64px] truncate text-center">
              {f.name}
            </div>
          </div>
        ))}
      </div>
    )}
    {/* Prompt Label Selector */}
    <div className="flex items-center gap-2 px-1 mb-1">
      <span className="text-xs text-slate-400">Prompt:</span>
      <select
        value={promptLabel}
        onChange={(e) => onPromptLabelChange(e.target.value)}
        data-testid="prompt-label-select"
        className="text-xs border border-slate-200 rounded px-2 py-0.5 bg-white text-slate-600 focus:outline-none focus:border-blue-400"
      >
        {promptLabels.map((label) => (
          <option key={label} value={label}>
            {label}
          </option>
        ))}
      </select>
    </div>
    <div className="flex gap-3 items-end">
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept={SUPPORTED_ACCEPT_STRING}
        onChange={onFileAttach}
        className="hidden"
        data-testid="file-input"
      />
      <button
        onClick={() => fileInputRef.current?.click()}
        disabled={isStreaming || !canAttachMore}
        title="파일 첨부 (이미지, PDF)"
        data-testid="attach-button"
        className="p-3 text-slate-500 hover:text-blue-600 hover:bg-blue-50 rounded-xl disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
      >
        <Paperclip size={20} />
      </button>
      {docRefEnabled && (
        <button
          onClick={() => setDocPickerOpen(true)}
          disabled={isStreaming || !canAttachMore}
          title="업로드된 문서 참조 (document_id 모드)"
          data-testid="doc-ref-button"
          className="p-3 text-slate-500 hover:text-indigo-600 hover:bg-indigo-50 rounded-xl disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          <Library size={20} />
        </button>
      )}
      <textarea
        value={input}
        onChange={(e) => onInputChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            onSend();
          }
        }}
        placeholder="메시지를 입력하세요... (Enter로 전송)"
        disabled={isStreaming}
        data-testid="chat-input"
        className="flex-1 border border-slate-200 rounded-xl px-4 py-3 resize-none focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 disabled:bg-slate-100 transition-all"
        rows={2}
      />
      {isStreaming ? (
        <button
          onClick={onAbort}
          data-testid="abort-button"
          className="bg-rose-600 text-white px-8 py-3 rounded-xl hover:bg-rose-700 transition-colors flex items-center gap-2 font-medium"
          title="스트리밍 중단"
        >
          <Square size={18} fill="currentColor" />
          중단
        </button>
      ) : (
        <button
          onClick={() => onSend()}
          disabled={blockSend || isPendingResponse || (!input.trim() && attachedFiles.length === 0)}
          data-testid="send-button"
          title={blockSend ? '응답 생성 중 — 잠시 후 다시 시도하세요' : undefined}
          className="bg-blue-600 text-white px-8 py-3 rounded-xl hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2 font-medium"
        >
          <Send size={18} />
          전송
        </button>
      )}
    </div>
    {docPickerOpen && selectedUserId && onAttachDocumentId && (
      <DocumentPickerModal
        userId={selectedUserId}
        remainingSlots={MAX_ATTACHMENTS - attachedFiles.length}
        onSelect={onAttachDocumentId}
        onClose={() => setDocPickerOpen(false)}
      />
    )}
  </div>
  );
});

ChatInputArea.displayName = 'ChatInputArea';

export default ChatInputArea;
